import { useCallback, useState } from 'react';
import catchCancel from 'helpers/catchCancel';
import axios from 'axios';
import { CROPWISE_PROXY_V2_URL } from 'utilities/apiConstants';
import { getAccessToken } from 'utilities/base-auth';

const useLoads = () => {
  const [loading, setLoading] = useState(false);
  const [loadingRetry, setLoadingRetry] = useState(false);
  const [hasActiveTasks, setHasActiveTasks] = useState(false);

  const getLoads = async ({
    limit = 50,
    page = 0,
    sortBy = 'name',
    sortDir = 'asc',
    seasonIds = [],
    cropIds,
    searchQuery,
    startDate,
    endDate,
    loadTypes,
    cropZoneIds,
    storageIds,
    saleIds
  }) => {
    setLoading(true);
    const orgId = localStorage.getItem('selectedOrganizationId');

    const body = {
      seasonIds,
      cropIds: cropIds?.length ? cropIds : undefined,
      searchQuery: searchQuery || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      loadTypes: loadTypes?.length ? loadTypes : undefined,
      storageIds: storageIds?.length ? storageIds : undefined,
      saleIds: saleIds?.length ? saleIds : undefined,
      cropZoneIds: cropZoneIds?.length ? cropZoneIds : undefined
    };

    return axios
      .post(`${CROPWISE_PROXY_V2_URL}/yield/list`, body, {
        headers: {
          'Content-Type': 'application/json',
          'cwf-context': JSON.stringify({
            organization_id: orgId
          }),
          Authorization: `Bearer ${getAccessToken()}`
        },
        params: {
          limit,
          page,
          sortBy,
          sortDir
        }
      })
      .then(({ data }) => {
        return data;
      })
      .catch(catchCancel)
      .catch(() => {
        return {};
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getLoadDetails = useCallback(loadId => {
    setLoading(true);
    const orgId = localStorage.getItem('selectedOrganizationId');

    const promise = axios.get(`${CROPWISE_PROXY_V2_URL}/yield/${loadId}`, {
      headers: {
        'Content-Type': 'application/json',
        'cwf-context': JSON.stringify({
          organization_id: orgId
        }),
        Authorization: `Bearer ${getAccessToken()}`
      }
    });

    return promise
      .then(({ data }) => data)
      .catch(catchCancel)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const deleteLoad = useCallback(({ id }) => {
    setLoading(true);
    const orgId = localStorage.getItem('selectedOrganizationId');

    const promise = axios.delete(`${CROPWISE_PROXY_V2_URL}/yield/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'cwf-context': JSON.stringify({
          organization_id: orgId
        }),
        Authorization: `Bearer ${getAccessToken()}`
      }
    });

    return promise
      .then(response => ({
        status: response.status,
        trackingId: response.headers?.['x-request-tracking-id']
      }))
      .catch(catchCancel)
      .finally(() => setLoading(false));
  }, []);

  const [yieldLoad, setYieldLoad] = useState();
  const [submitError, setSubmitError] = useState(null);

  const getLoadById = useCallback(id => {
    const orgId = localStorage.getItem('selectedOrganizationId');
    const headers = {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: orgId
      }),
      Authorization: `Bearer ${getAccessToken()}`
    };
    const promise = axios.get(`${CROPWISE_PROXY_V2_URL}/yield/${id}`, {
      headers
    });
    promise
      .then(({ data }) => {
        setYieldLoad(data);
      })
      .catch(catchCancel);
  }, []);

  const updateLoadById = useCallback((id, updatedData) => {
    setLoading(true);

    const orgId = localStorage.getItem('selectedOrganizationId');
    const headers = {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: orgId
      }),
      Authorization: `Bearer ${getAccessToken()}`
    };
    const promise = axios.put(
      `${CROPWISE_PROXY_V2_URL}/yield/${id}`,
      updatedData,
      {
        headers
      }
    );
    return promise
      .then(response => {
        setYieldLoad(response.data);
        return {
          data: response.data,
          trackingId: response.headers?.['x-request-tracking-id']
        };
      })
      .catch(catchCancel)
      .catch(error => {
        setSubmitError(error);
        throw error;
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const saveAllLoads = useCallback(async items => {
    const orgId = localStorage.getItem('selectedOrganizationId');

    const headers = {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: orgId
      }),
      Authorization: `Bearer ${getAccessToken()}`
    };

    const promise = axios.post(`${CROPWISE_PROXY_V2_URL}/yield/bulk`, items, {
      headers
    });

    return promise
      .then(response => ({
        data: response.data,
        trackingId: response.headers?.['x-request-tracking-id']
      }))
      .catch(catchCancel);
  }, []);

  const getTasksStatus = async ({ seasonIds = [] }) => {
    const orgId = localStorage.getItem('selectedOrganizationId');

    const body = {
      cropSeasonIds: seasonIds,
      scope: 'YIELD'
    };

    return axios
      .post(`${CROPWISE_PROXY_V2_URL}/request-tracking/list`, body, {
        headers: {
          'Content-Type': 'application/json',
          'cwf-context': JSON.stringify({
            organization_id: orgId
          }),
          Authorization: `Bearer ${getAccessToken()}`
        }
      })
      .then(({ data }) => {
        // Set flag to true if there's at least one task with PROCESSING or WAITING status
        const hasActiveProcessingTasks = Boolean(
          data &&
            Array.isArray(data) &&
            data.some(
              task => task.status === 'PROCESSING' || task.status === 'WAITING'
            )
        );
        setHasActiveTasks(hasActiveProcessingTasks);
        return data;
      })
      .catch(catchCancel)
      .catch(() => {
        setHasActiveTasks(false);
        return {};
      });
  };

  const updateTasksStatus = useCallback((id, seasonIds) => {
    const orgId = localStorage.getItem('selectedOrganizationId');
    const headers = {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: orgId
      }),
      Authorization: `Bearer ${getAccessToken()}`
    };
    const promise = axios.put(
      `${CROPWISE_PROXY_V2_URL}/request-tracking/acknowledge/${id}`,
      { cropSeasonIds: seasonIds },
      {
        headers
      }
    );
    return promise
      .then(({ status }) => {
        return {
          success: status >= 200 && status < 300,
          status,
          taskId: id
        };
      })
      .catch(catchCancel)
      .catch(error => {
        setSubmitError(error);
        return {
          success: false,
          error: error.response?.data || error.message,
          status: error.response?.status,
          taskId: id
        };
      });
  }, []);

  const retryTasksStatus = useCallback(id => {
    setLoadingRetry(true);
    const orgId = localStorage.getItem('selectedOrganizationId');
    const headers = {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: orgId
      }),
      Authorization: `Bearer ${getAccessToken()}`
    };
    const promise = axios.post(
      `${CROPWISE_PROXY_V2_URL}/request-tracking/retry/${id}`,
      { id },
      {
        headers
      }
    );
    return promise
      .then(response => {
        return {
          success: response.status >= 200 && response.status < 300,
          status: response.status,
          taskId: id,
          trackingId: response.headers?.['x-request-tracking-id']
        };
      })
      .catch(catchCancel)
      .catch(error => {
        setSubmitError(error);
        throw error;
      })
      .finally(() => {
        setLoadingRetry(false);
      });
  }, []);

  return {
    getLoadDetails,
    deleteLoad,
    getLoads,
    loading,
    getLoadById,
    yieldLoad,
    submitError,
    updateLoadById,
    saveAllLoads,
    getTasksStatus,
    updateTasksStatus,
    retryTasksStatus,
    loadingRetry,
    hasActiveTasks
  };
};

export default useLoads;
